// ViewPazienti.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Card, TextField, IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Snackbar, Alert, MenuItem } from '@mui/material';
import SortableTable, { HeadCell } from '../../components/SortableTable';
import { getPatients, updatePatient, deletePatient, createPatient } from '../../api/patients';
import { useUserProvider } from '../../providers/useUserProvider';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface Patient {
  id_utente: number;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  data_nascita: string;
  email: string;
  telefono: string;
  id_creatore: number;
}

const ViewPazienti: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState<Patient | null>(null);
  const [selected, setSelected] = useState<Patient | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{open:boolean; msg:string; sev:'success'|'error'|'info'}>({open:false,msg:'',sev:'success'});

  // stato per il create
  const [createOpen, setCreateOpen] = useState(false);
  const [newPatient, setNewPatient] = useState<any>({
    nome: '', cognome: '', codice_fiscale: '', data_nascita: '', email: '', telefono: '', password: '', genere: 'M', id_tipo_utente: 3
  });

  const { user } = useUserProvider();
  const navigate = useNavigate();

  const headCells: HeadCell<Patient>[] = useMemo(() => ([
    { id: 'id_utente', numeric: true, disablePadding: false, label: 'ID Utente' },
    { id: 'nome', numeric: false, disablePadding: false, label: 'Nome' },
    { id: 'cognome', numeric: false, disablePadding: false, label: 'Cognome' },
    { id: 'codice_fiscale', numeric: false, disablePadding: false, label: 'Codice Fiscale' },
    { id: 'data_nascita', numeric: false, disablePadding: false, label: 'Data di Nascita' },
    { id: 'email', numeric: false, disablePadding: false, label: 'Email' },
    { id: 'actions', label: 'Azioni', disableSort: true },   // 👈 nuova colonna
  ]), []);

  useEffect(() => {
    const fetchPatients = async () => {
      if (user.userId !== null) {
        try {
          const data = await getPatients(user.userType === 1 ? null : user.userId);
          setPatients(data);
          setFilteredPatients(data);
        } catch (error) {
          console.error('Error fetching patients:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchPatients();
  }, [user]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = patients.filter(p =>
      p.nome.toLowerCase().includes(term) ||
      p.cognome.toLowerCase().includes(term) ||
      p.id_utente.toString().includes(term) ||
      p.email?.toLowerCase().includes(term)
    );
    setFilteredPatients(filtered);
  }, [searchTerm, patients]);

  const handleView = (patientId: number) => navigate(`/patient/${patientId}`);

  const handleEditOpen = (row: Patient) => {
    setSelected(row);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelected(null);
  };

  const handleDeleteAsk = (row: Patient) => setDeleteOpen(row);
  const handleDeleteCancel = () => setDeleteOpen(null);

  const handleDeleteConfirm = async () => {
    if (!deleteOpen) return;
    try {
      await deletePatient(deleteOpen.id_utente);
      setPatients(prev => prev.filter(p => p.id_utente !== deleteOpen.id_utente));
      setToast({open:true,msg:'Paziente eliminato',sev:'success'});
    } catch (e) {
      console.error(e);
      setToast({open:true,msg:'Errore nell’eliminazione',sev:'error'});
    } finally {
      setDeleteOpen(null);
    }
  };

  const handleEditSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = {
        nome: selected.nome?.trim(),
        cognome: selected.cognome?.trim(),
        codice_fiscale: selected.codice_fiscale?.trim(),
        data_nascita: selected.data_nascita, // ISO string attesa dal BE
        email: selected.email?.trim(),
        telefono: selected.telefono?.trim(),
      };
      const updated = await updatePatient(selected.id_utente, payload);
      // aggiorno in-place
      setPatients(prev => prev.map(p => p.id_utente === selected.id_utente ? { ...p, ...updated } : p));
      setToast({open:true,msg:'Paziente aggiornato',sev:'success'});
      setEditOpen(false);
      setSelected(null);
    } catch (e) {
      console.error(e);
      setToast({open:true,msg:'Errore nel salvataggio',sev:'error'});
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSave = async () => {
    try {
      const res = await createPatient(newPatient);
      setToast({open:true,msg:res.message,sev:'success'});
      setCreateOpen(false);
      setNewPatient({ nome:'', cognome:'', codice_fiscale:'', data_nascita:'', email:'', telefono:'', password:'', genere:'M', id_tipo_utente:3 });
      const data = await getPatients(user.userType === 1 ? null : user.userId);
      setPatients(data);
      setFilteredPatients(data);
    } catch (e:any) {
      console.error(e);
      setToast({open:true,msg:e.message,sev:'error'});
    }
  };

  const renderRow = (row: Patient) => (
    <TableRow hover tabIndex={-1} key={row.id_utente}>
      <TableCell sx={{ textAlign: 'left', paddingLeft: '60px' }}>{row.id_utente}</TableCell>
      <TableCell>{row.nome}</TableCell>
      <TableCell>{row.cognome}</TableCell>
      <TableCell>{row.codice_fiscale}</TableCell>
      <TableCell>{new Date(row.data_nascita).toLocaleDateString()}</TableCell>
      <TableCell>{row.email}</TableCell>
      <TableCell>
        <Box display="flex" gap={1}>
          <Tooltip title="Visualizza">
            <IconButton onClick={() => handleView(row.id_utente)} size="small"><VisibilityIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Modifica">
            <IconButton onClick={() => handleEditOpen(row)} size="small"><EditIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Elimina">
            <IconButton onClick={() => handleDeleteAsk(row)} size="small" color="error"><DeleteIcon /></IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box padding="2rem">
      <Card sx={{ padding: '1rem', borderRadius: '1rem' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            label="Cerca"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ marginRight: '1rem' }}
          />
          <Button variant="contained" onClick={()=>setCreateOpen(true)}>Add User</Button>
        </Box>

        <SortableTable<Patient>
          rows={filteredPatients}
          headCells={headCells}
          renderRow={renderRow}
        />
      </Card>

      {/* Dialog Modifica */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Modifica paziente</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Nome" value={selected.nome || ''}
                  onChange={(e)=>setSelected({...selected!, nome:e.target.value})}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Cognome" value={selected.cognome || ''}
                  onChange={(e)=>setSelected({...selected!, cognome:e.target.value})}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Codice Fiscale" value={selected.codice_fiscale || ''}
                  onChange={(e)=>setSelected({...selected!, codice_fiscale:e.target.value.toUpperCase()})}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Data di nascita" type="date"
                  value={selected.data_nascita ? selected.data_nascita.substring(0,10) : ''}
                  onChange={(e)=>setSelected({...selected!, data_nascita:e.target.value})}
                  InputLabelProps={{ shrink: true }}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email" value={selected.email || ''}
                  onChange={(e)=>setSelected({...selected!, email:e.target.value})}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Telefono" value={selected.telefono || ''}
                  onChange={(e)=>setSelected({...selected!, telefono:e.target.value})}/>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Annulla</Button>
          <Button onClick={handleEditSave} disabled={saving} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Elimina */}
      <Dialog open={!!deleteOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confermi eliminazione?</DialogTitle>
        <DialogContent dividers>
          Stai per eliminare <b>{deleteOpen?.nome} {deleteOpen?.cognome}</b> (ID {deleteOpen?.id_utente}). L’azione è irreversibile.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Elimina</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Crea */}
      <Dialog open={createOpen} onClose={()=>setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo utente / medico</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Nome" value={newPatient.nome} onChange={e=>setNewPatient({...newPatient, nome:e.target.value})}/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Cognome" value={newPatient.cognome} onChange={e=>setNewPatient({...newPatient, cognome:e.target.value})}/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Codice Fiscale" value={newPatient.codice_fiscale} onChange={e=>setNewPatient({...newPatient, codice_fiscale:e.target.value.toUpperCase()})}/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Data di nascita" type="date" value={newPatient.data_nascita} onChange={e=>setNewPatient({...newPatient, data_nascita:e.target.value})} InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Email" value={newPatient.email} onChange={e=>setNewPatient({...newPatient, email:e.target.value})}/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Telefono" value={newPatient.telefono} onChange={e=>setNewPatient({...newPatient, telefono:e.target.value})}/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Password" type="password" value={newPatient.password} onChange={e=>setNewPatient({...newPatient, password:e.target.value})}/></Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth select label="Tipo utente" value={newPatient.id_tipo_utente} onChange={e=>setNewPatient({...newPatient, id_tipo_utente:Number(e.target.value)})}>
                <MenuItem value={3}>Paziente</MenuItem>
                <MenuItem value={5}>Medico</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setCreateOpen(false)}>Annulla</Button>
          <Button onClick={handleCreateSave} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={()=>setToast(s=>({...s,open:false}))}>
        <Alert severity={toast.sev} variant="filled" onClose={()=>setToast(s=>({...s,open:false}))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewPazienti;
